import { useCallback, useRef } from "react";
/**
 * Hook to capture the last focused element's id (such as a button or a link)
 * and restore focus to it later via selection through `document.getElementById()`.
 * Used when the target element is mounted / unmounted and needs to regain selection.
 * This structure is useful for managing multiple possible triggers for a single modal/popover opening.
 *
 * @template T The type of the element being focused, defaulting to `HTMLElement`
 * @returns An object containing:
 * - `captureLastFocusedElement(el)`: Call this with the element before opening a modal or popup
 * - `restoreFocus()`: Call this after closing the modal or popup to return focus.
 */
var useLastFocusedElement = function () {
    var lastFocusedId = useRef(null);
    var captureLastFocusedElement = useCallback(function (el) {
        if (el === null || el === void 0 ? void 0 : el.id)
            lastFocusedId.current = el === null || el === void 0 ? void 0 : el.id;
    }, []);
    var restoreFocus = useCallback(function () {
        if (lastFocusedId === null || lastFocusedId === void 0 ? void 0 : lastFocusedId.current) {
            var element_1 = document.getElementById(lastFocusedId === null || lastFocusedId === void 0 ? void 0 : lastFocusedId.current);
            setTimeout(function () {
                var _a;
                (_a = element_1 === null || element_1 === void 0 ? void 0 : element_1.focus) === null || _a === void 0 ? void 0 : _a.call(element_1);
                lastFocusedId.current = null;
            }, 50);
        }
    }, []);
    return {
        captureLastFocusedElement: captureLastFocusedElement,
        restoreFocus: restoreFocus,
    };
};
export default useLastFocusedElement;
